/// App Store discovery card on the business dashboard: search positions per
/// storefront, top-chart places, and the App Store funnel (impressions, page
/// views, downloads), each against the previous day. Types mirror
/// functions/src/appStoreDiscoveryBlock.ts and appStoreDiscoveryCore.ts
/// (duplicated across the functions/web boundary by convention).
import { useState } from 'react'
import { httpsCallable } from '../firebase/callable'
import { firebaseFunctions } from '../firebase/app'

type Move =
  | { kind: 'first' }
  | { kind: 'absent' }
  | { kind: 'new' }
  | { kind: 'out' }
  | { kind: 'same' }
  | { kind: 'up'; by: number }
  | { kind: 'down'; by: number }

interface RankCell {
  rank: number | null
  prev: number | null | undefined
  move: Move
}

interface MoveLine {
  country: string
  key: string
  rank: number | null
  prev: number | null
  move: Move
}

interface FunnelCounts {
  impressions: number
  pageViews: number
  firstDownloads: number
  redownloads: number
}

interface FunnelRow {
  territory: string
  today: FunnelCounts
  prev: FunnelCounts | null
}

type FunnelSource = 'search' | 'browse' | 'appReferrer' | 'webReferrer' | 'other'

export interface DiscoveryLive {
  source: 'live'
  date: string | null
  prevDate: string | null
  search: {
    countries: string[]
    keywords: string[]
    rows: Record<string, Record<string, RankCell>>
    biggestMoves: MoveLine[]
    errors: number
  } | null
  charts: {
    placements: MoveLine[]
    checked: number
    expected: number
    errors: number
    labels: Record<string, string>
  } | null
  funnel: {
    date: string
    prevDate: string | null
    complete: boolean
    total: FunnelRow
    territories: FunnelRow[]
    bySource: { source: FunnelSource; today: FunnelCounts; prev: FunnelCounts | null }[]
  } | null
  asc: { status: string; requestedAt: number | null; lastRunAt: number | null; lastError: string | null }
  note: string
}

const callStartReports = httpsCallable<unknown, { status: string; requestId?: string; message?: string }>(
  firebaseFunctions,
  'startAscAnalyticsReports',
)

const SOURCE_LABEL: Record<FunnelSource, string> = {
  search: 'App Store search',
  browse: 'App Store browse',
  appReferrer: 'App referrer',
  webReferrer: 'Web referrer',
  other: 'Other',
}

/// Movement of a position since the previous day. Positions count down, so a
/// climb shows as a plus.
function MoveChip({ move }: { move: Move }) {
  switch (move.kind) {
    case 'up':
      return <span className="text-emerald-400">+{move.by}</span>
    case 'down':
      return <span className="text-rose-400">-{move.by}</span>
    case 'new':
      return <span className="text-emerald-400">new</span>
    case 'out':
      return <span className="text-rose-400">out</span>
    case 'same':
      return <span className="text-slate-600">=</span>
    default:
      return null
  }
}

/// Change in a count since the previous day (higher is better).
function CountDelta({ today, prev }: { today: number; prev: number | undefined }) {
  if (prev === undefined) return null
  const d = today - prev
  if (d === 0) return <span className="ml-1 text-[10px] text-slate-600">=</span>
  return (
    <span className={`ml-1 text-[10px] ${d > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
      {d > 0 ? `+${d.toLocaleString()}` : `-${Math.abs(d).toLocaleString()}`}
    </span>
  )
}

function rankText(rank: number | null): string {
  return rank === null ? '·' : String(rank)
}

function SearchSection({ data }: { data: NonNullable<DiscoveryLive['search']> }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold text-slate-300">Search position</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-slate-500">
            <tr>
              <th className="sticky left-0 bg-slate-900 py-1 pr-2 text-left font-medium">Term</th>
              {data.countries.map((cc) => (
                <th key={cc} className="px-1 py-1 text-center font-medium">
                  {cc}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.keywords.map((kw) => (
              <tr key={kw} className="border-t border-white/5">
                <td className="sticky left-0 whitespace-nowrap bg-slate-900 py-1 pr-2 text-slate-300">{kw}</td>
                {data.countries.map((cc) => {
                  const cell = data.rows[kw]?.[cc]
                  return (
                    <td key={cc} className="px-1 py-1 text-center tabular-nums">
                      {cell ? (
                        <div className="leading-tight">
                          <div className={cell.rank !== null && cell.rank <= 10 ? 'font-semibold text-slate-100' : 'text-slate-400'}>
                            {rankText(cell.rank)}
                          </div>
                          <div className="text-[9px]">
                            <MoveChip move={cell.move} />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-700" title="This check failed today">
                          ?
                        </span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[10px] text-slate-500">
        Position in the top 200 results; · means not in them.
        {data.errors === 1 ? ' 1 check failed today and shows as ?.' : ''}
        {data.errors > 1 ? ` ${data.errors} checks failed today and show as ?.` : ''}
      </p>
      {data.biggestMoves.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] font-medium text-slate-400">Biggest moves</div>
          <ul className="mt-1 space-y-0.5 text-[11px] text-slate-300">
            {data.biggestMoves.map((m) => (
              <li key={`${m.key}-${m.country}`}>
                "{m.key}" {m.country}: {rankText(m.prev)} to {rankText(m.rank)} <MoveChip move={m.move} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function ChartsSection({ data }: { data: NonNullable<DiscoveryLive['charts']> }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold text-slate-300">Top charts</h3>
      {data.placements.length === 0 ? (
        <p className="text-[11px] text-slate-400">
          Not in any top 100 today. Checked {data.checked} of {data.expected} chart lists
          {data.errors > 0 ? ` (${data.errors} failed)` : ''}.
        </p>
      ) : (
        <table className="w-full text-[11px]">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 pr-2 text-left font-medium">Storefront</th>
              <th className="py-1 px-2 text-left font-medium">Chart</th>
              <th className="py-1 px-2 text-right font-medium">Position</th>
              <th className="py-1 pl-2 text-right font-medium">Move</th>
            </tr>
          </thead>
          <tbody>
            {data.placements.map((p) => (
              <tr key={`${p.country}-${p.key}`} className="border-t border-white/5">
                <td className="py-1 pr-2 text-slate-300">{p.country}</td>
                <td className="py-1 px-2 text-slate-400">{data.labels[p.key] ?? p.key}</td>
                <td className="py-1 px-2 text-right tabular-nums text-slate-200">{rankText(p.rank)}</td>
                <td className="py-1 pl-2 text-right">
                  <MoveChip move={p.move} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function StartReports({ asc }: { asc: DiscoveryLive['asc'] }) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const start = async () => {
    setBusy(true)
    try {
      const res = await callStartReports({})
      const s = res.data.status
      setResult(
        s === 'created' || s === 'exists'
          ? 'Requested. Apple generates the first reports in 1 to 2 days; this card fills in after the next 07:30 ET pull.'
          : res.data.message ?? `Apple answered: ${s}`,
      )
    } catch (err) {
      setResult(`Failed: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }
  if (asc.status === 'live') {
    return (
      <p className="text-[11px] text-slate-400">
        Apple's reports are running. A day appears once both its impressions and its downloads have arrived.
      </p>
    )
  }
  if (asc.status === 'requested' || asc.status === 'awaiting_reports') {
    const when = asc.requestedAt ? new Date(asc.requestedAt).toLocaleDateString() : 'recently'
    return (
      <p className="text-[11px] text-slate-400">
        Apple's daily reports were requested {when}. The first ones arrive 1 to 2 days after the request.
      </p>
    )
  }
  return (
    <div className="space-y-2 text-[11px] text-slate-400">
      {asc.status === 'error' && asc.lastError && (
        <p className="text-rose-300">The last pull failed: {asc.lastError}</p>
      )}
      <p>
        Impressions, product page views and downloads by country come from App Store Connect's analytics reports.
        Apple starts them once, on request. The 07:30 ET pull asks for them when none exist; this button asks now.
      </p>
      <button
        onClick={start}
        disabled={busy}
        className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/10 hover:bg-slate-700 disabled:opacity-50"
      >
        {busy ? 'Requesting…' : "Start Apple's daily reports"}
      </button>
      {result && <p className="text-slate-300">{result}</p>}
    </div>
  )
}

function FunnelSection({ data }: { data: DiscoveryLive }) {
  const f = data.funnel
  if (!f) {
    return (
      <div>
        <h3 className="mb-1 text-xs font-semibold text-slate-300">App Store funnel</h3>
        <StartReports asc={data.asc} />
      </div>
    )
  }
  const cols: { key: keyof FunnelCounts; label: string }[] = [
    { key: 'impressions', label: 'Impressions' },
    { key: 'pageViews', label: 'Page views' },
    { key: 'firstDownloads', label: 'First downloads' },
    { key: 'redownloads', label: 'Redownloads' },
  ]
  const row = (label: string, r: { today: FunnelCounts; prev: FunnelCounts | null }, strong = false) => (
    <tr key={label} className="border-t border-white/5">
      <td className={`py-1 pr-2 ${strong ? 'font-semibold text-slate-100' : 'text-slate-300'}`}>{label}</td>
      {cols.map((c) => (
        <td key={c.key} className="py-1 px-2 text-right tabular-nums text-slate-200">
          {r.today[c.key].toLocaleString()}
          <CountDelta today={r.today[c.key]} prev={r.prev ? r.prev[c.key] : undefined} />
        </td>
      ))}
    </tr>
  )
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold text-slate-300">
        App Store funnel · {f.date}
        {f.prevDate ? ` vs ${f.prevDate}` : ''}
        {!f.complete && <span className="ml-1 font-normal text-amber-300/80">(Apple is still completing this day)</span>}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 pr-2 text-left font-medium">Territory</th>
              {cols.map((c) => (
                <th key={c.key} className="py-1 px-2 text-right font-medium">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row('All', f.total, true)}
            {f.territories.map((t) => row(t.territory, t))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 pr-2 text-left font-medium">Source</th>
              {cols.map((c) => (
                <th key={c.key} className="py-1 px-2 text-right font-medium">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{f.bySource.map((s) => row(SOURCE_LABEL[s.source], s))}</tbody>
        </table>
      </div>
    </div>
  )
}

export function DiscoveryCard({ data }: { data: DiscoveryLive }) {
  return (
    <div className="rounded-lg bg-slate-900 p-4 ring-1 ring-white/10">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">App Store discovery</h2>
        <span className="text-[11px] text-slate-500">
          {data.date ? `sweep ${data.date}${data.prevDate ? ` vs ${data.prevDate}` : ' (no sweep the day before, so no movement)'}` : 'no sweep yet'}
        </span>
      </div>
      <div className="space-y-5">
        {data.search ? (
          <SearchSection data={data.search} />
        ) : (
          <p className="text-[11px] text-slate-400">The first sweep runs at 06:15 ET.</p>
        )}
        {data.charts && <ChartsSection data={data.charts} />}
        <FunnelSection data={data} />
      </div>
      <p className="mt-3 text-[11px] text-slate-500">{data.note}</p>
    </div>
  )
}
